import Link from "@/components/Link";
import { AppRoutes } from "@/lib/routeResolver";
import type { PressRelease } from "@/lib/pressreleases";
import { formatPressDateShort, stripHtml } from "@/lib/pressreleases";

interface PressReleaseCardProps {
  press: PressRelease;
  destinationName?: string;
}

export default function PressReleaseCard({
  press,
  destinationName,
}: PressReleaseCardProps) {
  const { month, day, year } = formatPressDateShort(press.createdDate);
  const excerpt = stripHtml(press.description, 180);
  const hasExternalLink =
    press.isExternalLink && press.externalLink?.length > 0;
  const href = hasExternalLink
    ? press.externalLink
    : AppRoutes.pressRelease(press.pageUrl);

  // Only real media coverages from the API — no placeholder fallbacks.
  const mediaCoverages = press.mediaCoverageObj ?? [];

  // Resolve destination: prefer prop, then API field
  const destination = destinationName || press.destinationName;

  return (
    <div className="pr-card">
      {/* Image section */}
      <div className="pr-card-img">
        {hasExternalLink ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            title={press.title}
            style={{ display: "block", lineHeight: 0 }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={press.image || "/central.jpg"} alt={press.title} loading="lazy" decoding="async" />
          </a>
        ) : (
          <Link href={href} title={press.title} style={{ display: "block", lineHeight: 0 }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={press.image || "/central.jpg"} alt={press.title} loading="lazy" decoding="async" />
          </Link>
        )}
        <div className="pr-card-date">
          <span className="pr-card-date-day">{day}</span>
          <span className="pr-card-date-month">{month}</span>
          <span className="pr-card-date-year">{year}</span>
        </div>
        {destination && (
          <span className="pr-card-destination">
            <i className="fa-solid fa-location-dot"></i>
            {destination}
          </span>
        )}
      </div>

      {/* Content section */}
      <div className="pr-card-body">
        <h3 className="pr-card-title">
          {hasExternalLink ? (
            <a href={href} target="_blank" rel="noreferrer" title={press.title}>
              {press.title}
            </a>
          ) : (
            <Link href={href} title={press.title}>{press.title}</Link>
          )}
        </h3>

        {excerpt && <p className="pr-card-excerpt">{excerpt}</p>}

        {/* Media Coverage Links — only when the API provides real coverages */}
        {mediaCoverages.length > 0 && (
          <div className="pr-card-media">
            <span className="pr-card-media-label">
              <i className="fa-solid fa-newspaper"></i>
              Media Coverage
            </span>
            <div className="pr-card-media-links">
              {mediaCoverages.map((mc, i) => (
                <a
                  key={i}
                  href={mc.externalLink}
                  target="_blank"
                  rel="noreferrer"
                  className="pr-card-media-link"
                  title={mc.title}
                >
                  {mc.title}
                  <i className="fa-solid fa-arrow-up-right-from-square"></i>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="pr-card-footer">
          {hasExternalLink ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="pr-card-readmore"
              title={`Read More about ${press.title}`}
            >
              Read More
              <span className="visually-hidden">{` about ${press.title}`}</span>
              <i className="fa-solid fa-arrow-right"></i>
            </a>
          ) : (
            <Link
              href={href}
              className="pr-card-readmore"
              title={`Read More about ${press.title}`}
            >
              Read More
              <span className="visually-hidden">{` about ${press.title}`}</span>
              <i className="fa-solid fa-arrow-right"></i>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
